import { paramCase } from 'src/utils/change-case';

import { _id, _postTitles } from 'src/_mock/assets';

// ----------------------------------------------------------------------

const MOCK_ID = _id[1];

const MOCK_TITLE = _postTitles[2];

const ROOTS = {
  AUTH: '/auth',
  AUTH_DEMO: '/auth-demo',
  DASHBOARD: '/dashboard',
  APP: '',
};

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/error/403',
  page404: '/error/404',
  page500: '/error/500',
  components: '/components',
  docs: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
  zoneStore: 'https://mui.com/store/items/zone-landing-page/',
  minimalStore: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  figma: 'https://www.figma.com/design/cAPz4pYPtQEXivqe11EcDE/%5BPreview%5D-Minimal-Web.v6.0.0',
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id) => `/product/${id}`,
    demo: { details: `/product/${MOCK_ID}` },
  },
  post: {
    root: `/post`,
    details: (title) => `/post/${paramCase(title)}`,
    demo: { details: `/post/${paramCase(MOCK_TITLE)}` },
  },
  // AUTH
  auth: {
    login: `${ROOTS.AUTH}/login`,
    forgotPassword: `${ROOTS.AUTH}/forgot-password`,
    amplify: {
      login: `${ROOTS.AUTH}/amplify/login`,
      verify: `${ROOTS.AUTH}/amplify/verify`,
      register: `${ROOTS.AUTH}/amplify/register`,
      newPassword: `${ROOTS.AUTH}/amplify/new-password`,
      forgotPassword: `${ROOTS.AUTH}/amplify/forgot-password`,
    },
    jwt: {
      login: `${ROOTS.AUTH}/jwt/login`,
      register: `${ROOTS.AUTH}/jwt/register`,
      forgotPassword: `${ROOTS.AUTH}/jwt/forgot-password`,
      newPassword: `${ROOTS.AUTH}/jwt/new-password`,
    },
    firebase: {
      signIn: `${ROOTS.AUTH}/firebase/sign-in`,
      verify: `${ROOTS.AUTH}/firebase/verify`,
      signUp: `${ROOTS.AUTH}/firebase/sign-up`,
      resetPassword: `${ROOTS.AUTH}/firebase/reset-password`,
    },
    auth0: {
      signIn: `${ROOTS.AUTH}/auth0/sign-in`,
    },
    supabase: {
      signIn: `${ROOTS.AUTH}/supabase/sign-in`,
      verify: `${ROOTS.AUTH}/supabase/verify`,
      signUp: `${ROOTS.AUTH}/supabase/sign-up`,
      updatePassword: `${ROOTS.AUTH}/supabase/update-password`,
      resetPassword: `${ROOTS.AUTH}/supabase/reset-password`,
    },
  },
  authDemo: {
    split: {
      signIn: `${ROOTS.AUTH_DEMO}/split/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/split/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/split/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/split/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/split/verify`,
    },
    centered: {
      signIn: `${ROOTS.AUTH_DEMO}/centered/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/centered/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/centered/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/centered/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/centered/verify`,
    },
  },
  // DASHBOARD
  dashboard: {
    root: '/',
    asset_details: (id) => `${ROOTS.APP}/view/${id}`,
    mail: `${ROOTS.DASHBOARD}/mail`,
    chat: `${ROOTS.DASHBOARD}/chat`,
    blank: `${ROOTS.DASHBOARD}/blank`,
    kanban: `${ROOTS.DASHBOARD}/kanban`,
    calendar: `${ROOTS.DASHBOARD}/calendar`,
    fileManager: `${ROOTS.DASHBOARD}/file-manager`,
    permission: `${ROOTS.DASHBOARD}/permission`,
    general: {
      app: `${ROOTS.DASHBOARD}/app`,
      ecommerce: `${ROOTS.DASHBOARD}/ecommerce`,
      analytics: `${ROOTS.DASHBOARD}/analytics`,
      banking: `${ROOTS.DASHBOARD}/banking`,
      booking: `${ROOTS.DASHBOARD}/booking`,
      file: `${ROOTS.DASHBOARD}/file`,
      course: `${ROOTS.DASHBOARD}/course`,
    },
    user: {
      root: `${ROOTS.DASHBOARD}/user`,
      new: `${ROOTS.DASHBOARD}/user/new`,
      list: `${ROOTS.DASHBOARD}/user/list`,
      cards: `${ROOTS.DASHBOARD}/user/cards`,
      profile: `${ROOTS.DASHBOARD}/user/profile`,
      account: `${ROOTS.DASHBOARD}/user/account`,
      edit: (id) => `${ROOTS.DASHBOARD}/user/${id}/edit`,
      demo: {
        edit: `${ROOTS.DASHBOARD}/user/${MOCK_ID}/edit`,
      },
    },
    product: {
      root: `${ROOTS.DASHBOARD}/product`,
      new: `${ROOTS.DASHBOARD}/product/new`,
      details: (id) => `${ROOTS.DASHBOARD}/product/${id}`,
      edit: (id) => `${ROOTS.DASHBOARD}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/product/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/product/${MOCK_ID}/edit`,
      },
    },
    invoice: {
      root: `${ROOTS.DASHBOARD}/invoice`,
      new: `${ROOTS.DASHBOARD}/invoice/new`,
      details: (id) => `${ROOTS.DASHBOARD}/invoice/${id}`,
      edit: (id) => `${ROOTS.DASHBOARD}/invoice/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.DASHBOARD}/post`,
      new: `${ROOTS.DASHBOARD}/post/new`,
      details: (title) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}`,
      edit: (title) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}`,
        edit: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.DASHBOARD}/order`,
      details: (id) => `${ROOTS.DASHBOARD}/order/${id}`,
      demo: {
        details: `${ROOTS.DASHBOARD}/order/${MOCK_ID}`,
      },
    },
    job: {
      root: `${ROOTS.DASHBOARD}/job`,
      new: `${ROOTS.DASHBOARD}/job/new`,
      details: (id) => `${ROOTS.DASHBOARD}/job/${id}`,
      edit: (id) => `${ROOTS.DASHBOARD}/job/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/job/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/job/${MOCK_ID}/edit`,
      },
    },
    tour: {
      root: `${ROOTS.DASHBOARD}/tour`,
      new: `${ROOTS.DASHBOARD}/tour/new`,
      details: (id) => `${ROOTS.DASHBOARD}/tour/${id}`,
      edit: (id) => `${ROOTS.DASHBOARD}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}/edit`,
      },
    },
  },
  app: {
    root: ROOTS.APP,
    dashboards: {
      asset_admin_dashboard: `${ROOTS.APP}/asset-admin/dashboard`,
      it_support_dashboard: `${ROOTS.APP}/it-support/dashboard`,
      technician_dashboard: `${ROOTS.APP}/technician/dashboard`,
      user_dashboard_admin: `${ROOTS.APP}/user/dashboard/admin`,
      cmms_dashboard: `${ROOTS.APP}/cmms-dashboard`,
    },
    profile: {
      root: `${ROOTS.APP}/profile/view`,
    },
    amc_providers: {
      root: `${ROOTS.APP}/amc-providers`,
    },
    asset: {
      root: `${ROOTS.APP}/asset`,
      manage_asset: `${ROOTS.APP}/asset`,
      item: `${ROOTS.APP}/asset/manage_item`,
      new_item: (assetCategory) =>
        `${ROOTS.APP}/asset/manage_item/new?assetCategory=${assetCategory}`,
      new: `${ROOTS.APP}/asset/new`,
      hardware_new: `${ROOTS.APP}/asset/hardware-new`,
      software_new: `${ROOTS.APP}/asset/software-new`,
      others_new: `${ROOTS.APP}/asset/others-new`,
      view_item: (id, assetCategory) =>
        `${ROOTS.APP}/asset/manage_item/view/${id}?assetCategory=${assetCategory}`,
      edit_item: (id, assetCategory) =>
        `${ROOTS.APP}/asset/manage_item/edit/${id}?assetCategory=${assetCategory}`,
      view: (id) => `${ROOTS.APP}/asset/view/${id}`,
      hardware_view: (id) => `${ROOTS.APP}/asset/hardware-view/${id}`,
      software_view: (id) => `${ROOTS.APP}/asset/software-view/${id}`,
      others_view: (id) => `${ROOTS.APP}/asset/others-view/${id}`,
      edit: (id) => `${ROOTS.APP}/asset/edit/${id}`,
      edit_hardware: (id) => `${ROOTS.APP}/asset/edit-hardware/${id}`,
      edit_software: (id) => `${ROOTS.APP}/asset/edit-software/${id}`,
      edit_others: (id) => `${ROOTS.APP}/asset/edit-others/${id}`,
      expiry: `${ROOTS.APP}/asset/expiry`,
    },
    asset_management: {
      root: `${ROOTS.APP}/asset-management`,
      // hardware
      hardware_assets: `${ROOTS.APP}/asset-management/hardware-assets`,
      hardware_assets_new: (id) => `${ROOTS.APP}/asset-management/hardware-assets/new/${id}`,
      hardware_assets_view: (id) => `${ROOTS.APP}/asset-management/hardware-assets/view/${id}`,
      hardware_assets_edit: (id) => `${ROOTS.APP}/asset-management/hardware-assets/edit/${id}`,
      hardware_assets_items: `${ROOTS.APP}/asset-management/hardware-assets/items`,
      hardware_assets_items_new: (assetCategory) =>
        `${ROOTS.APP}/asset-management/hardware-assets/items/new?assetCategory=${assetCategory}`,
      hardware_assets_items_view: (id, assetCategory) =>
        `${ROOTS.APP}/asset-management/hardware-assets/items/view/${id}?assetCategory=${assetCategory}`,
      hardware_assets_items_edit: (id, assetCategory) =>
        `${ROOTS.APP}/asset-management/hardware-assets/items/edit/${id}?assetCategory=${assetCategory}`,
      hardware_assets_alerts: `${ROOTS.APP}/asset-management/hardware-assets/alerts`,
      hardware_assets_pdf: `${ROOTS.APP}/asset-management/hardware-assets/pdf`,
      // software
      software_assets: `${ROOTS.APP}/asset-management/software-assets`,
      software_assets_new: (id) => `${ROOTS.APP}/asset-management/software-assets/new/${id}`,
      software_assets_view: (id) => `${ROOTS.APP}/asset-management/software-assets/view/${id}`,
      software_assets_edit: (id) => `${ROOTS.APP}/asset-management/software-assets/edit/${id}`,
      software_assets_alerts: `${ROOTS.APP}/asset-management/software-assets/alerts`,
      software_timelines: `${ROOTS.APP}/asset-management/software-assets/timelines`,
      // other
      other_assets: `${ROOTS.APP}/asset-management/other-assets`,
      other_assets_new: (id) => `${ROOTS.APP}/asset-management/other-assets/new/${id}`,
      other_assets_view: (id) => `${ROOTS.APP}/asset-management/other-assets/view/${id}`,
      other_assets_edit: (id) => `${ROOTS.APP}/asset-management/other-assets/edit/${id}`,
      other_assets_items: `${ROOTS.APP}/asset-management/other-assets/items`,
      other_assets_items_new: (assetCategory) =>
        `${ROOTS.APP}/asset-management/other-assets/items/new?assetCategory=${assetCategory}`,
      other_assets_items_view: (id, assetCategory) =>
        `${ROOTS.APP}/asset-management/other-assets/items/view/${id}?assetCategory=${assetCategory}`,
      other_assets_items_edit: (id, assetCategory) =>
        `${ROOTS.APP}/asset-management/other-assets/items/edit/${id}?assetCategory=${assetCategory}`,
      other_assets_alerts: `${ROOTS.APP}/asset-management/other-assets/alerts`,
      other_assets_pdf: `${ROOTS.APP}/asset-management/other-assets/pdf`,
      // all asset
      all_assets: `${ROOTS.APP}/asset-management/all-assets`,
    },
    settings: {
      root: `${ROOTS.APP}/settings`,
      users: `${ROOTS.APP}/settings/users`,
      users_view: (id) => `${ROOTS.APP}/settings/users/view/${id}`,
      users_create: `${ROOTS.APP}/settings/users/create`,
      users_edit: (id) => `${ROOTS.APP}/settings/users/edit/${id}`,
      make: `${ROOTS.APP}/settings/make`,
      vendor: `${ROOTS.APP}/settings/vendor`,
      location: `${ROOTS.APP}/settings/location`,
      asset_type: `${ROOTS.APP}/settings/asset_type`,
      asset_id: `${ROOTS.APP}/settings/asset_id`,
      ticket: `${ROOTS.APP}/settings/stage`,
      asset_config: `${ROOTS.APP}/asset-config`,
      checklist: `${ROOTS.APP}/settings/checklist`,
      asset_checklist: `${ROOTS.APP}/settings/asset_checklist`,
      create_asset_checklist: `${ROOTS.APP}/settings/asset_checklist/create`,
      edit_asset_checklist: (id) => `${ROOTS.APP}/settings/asset_checklist/edit/${id}`,
      view_asset_checklist: (id) => `${ROOTS.APP}/settings/asset_checklist/view/${id}`,
      asset_type_detail: (id) => `${ROOTS.APP}/settings/asset_type/view/${id} `,
      tag_list: `/settings/tags`,
      company: `${ROOTS.APP}/settings/company`,
      currency_manager: `${ROOTS.APP}/settings/currency-manager`,
      vendor_resellers: `/settings/vendor-reseller`,
      vendor_reseller_view: (id) => `/settings/vendor-reseller/view/${id}`,
      location_view: (country) => `/settings/location/view/${country}`,
      cmms_asset_type_management: `/asset-config/cmms`,
      it_assets_asset_type_management: `/asset-config/it-assets`,
    },
    users: {
      root: `${ROOTS.APP}/users`,
      view: (id) => `${ROOTS.APP}/users/view/${id}`,
      asset_view: (id) => `${ROOTS.APP}/users/asset/view/${id}`,
    },
    assets: {
      root: `${ROOTS.APP}/user-assets`,
      new: `${ROOTS.APP}/user-assets/new-request`,
      view: (id) => `${ROOTS.APP}/user-assets/view/${id}`,
      edit: (id) => `${ROOTS.APP}/user-assets/edit-request/${id}`,
    },
    userTickets: {
      root: `${ROOTS.APP}/user-tickets`,
      new: (id) => `${ROOTS.APP}/user-tickets/new-ticket/${id}`,
      edit: (id) => `${ROOTS.APP}/user-tickets/raise-ticket/${id}`,
      view: (id) => `${ROOTS.APP}/user-tickets/view/${id}`,
    },
    tickets: {
      root: `${ROOTS.APP}/ticket`,
      approval_list: `/ticket/approval`,
      ticket_view: (id) => `/ticket/approval/view/${id}`,
      ticket_list: '/ticket/list',
      closed_ticket_list: '/ticket/closed_list',
      modification: '/ticket/modification',
      technician_approval_list: '/ticket/technician_approval',
      // asset_view: (id) => `${ROOTS.APP}/users/asset/view/${id}`,
      approval_request: '/ticket/requests-approval',
      view_approval_request: (id) => `/ticket/requests-approval/view/${id}`,
      manager_ticket: '/ticket/my-ticket',
      manager_approvalList: `/ticket/my-ticket/approval-list`,
      manager_allTickets: `/ticket/my-ticket/all-tickets`,
      manager_myTickets: `/ticket/my-ticket/ticket-list`,
      manager_myTicketView: (id) => `/ticket/my-ticket/ticket-list/view/${id}`,
      manager_approvalListDetails: (id) => `/ticket/my-ticket/approval-list/view/${id}`,
      manager_allTicketDetails: (id) => `/ticket/my-ticket/all-tickets/view/${id}`,
      it_support_list: `/ticket/it-support/list`,
      it_support_view: (id) => `/ticket/it-support/view/${id}`,
      incident_ticket_list: '/incident-ticket/list',
      incident_closed_list: '/incident-ticket/closed_list',
      incident_modification_list: '/incident-ticket/modification',
      technician_incident_list: '/incident-ticket/technician_list',
    },
    approval: {
      root: `${ROOTS.APP}/approval`,
      new: `${ROOTS.APP}/approval/new-request`,
      view: (id) => `${ROOTS.APP}/approval/view/${id}`,
      raise_ticket: (id) => `${ROOTS.APP}/approval/raise-ticket/${id}`,
      editRequest: (id) => `${ROOTS.APP}/approval/edit-request/${id}`,
      editTicket: (id) => `${ROOTS.APP}/approval/edit-ticket/${id}`,
    },
    approvalManager: {
      myTicket_list: `${ROOTS.APP}/manager-approval/ticket-list`,
      view: (id) => `${ROOTS.APP}/manager-approval/ticket-list/view/${id}`,
    },
    assetSpendManagement: {
      root: `${ROOTS.APP}/asset-spend-management`,
      assetSpendOverview: `${ROOTS.APP}/asset-spend-management/asset-spend/overview`,
      userSpendList: `${ROOTS.APP}/asset-spend-management/user-spend`,
      userSpendView: (id, path) =>
        `${ROOTS.APP}/asset-spend-management/user-spend/view/${id}?returnTo=${path}`,
      userHardwareAssetView: (id) =>
        `/asset-spend-management/user-spend/hardware-assets/view/${id}`,
      userSoftwareAssetView: (id) =>
        `/asset-spend-management/user-spend/software-assets/view/${id}`,
      userOtherAssetView: (id) => `/asset-spend-management/user-spend/other-assets/view/${id}`,
      userDynamicAssetView: (id, assetCategory) =>
        `/asset-spend-management/user-spend/dynamic-assets/${assetCategory}/view/${id}`,
    },
    subscriptionAdmin: {
      root: `${ROOTS.APP}/subscription`,
    },
    userLogin: {
      root: `${ROOTS.APP}/user`,
      dashboard: `${ROOTS.APP}/user/dashboard`,
      // -------user-assets--------
      assetList: `${ROOTS.APP}/user/user-assets`,
      assetNewReq: `${ROOTS.APP}/user/user-assets/new-request`,
      assetView: (id) => `${ROOTS.APP}/user/user-assets/view/${id}`,
      assetEditReq: (id) => `${ROOTS.APP}/user/user-assets/edit-request/${id}`,
      // -------user-tickets--------
      ticketList: `${ROOTS.APP}/user/tickets-list`,
      ticketNew: `${ROOTS.APP}/user/general-ticket/new`,
      ticketView: (id) => `${ROOTS.APP}/user/tickets/view/${id}`,
      ticketRaise: (id) => `${ROOTS.APP}/user/new-ticket/${id}`,
      ticketEdit: (id) => `${ROOTS.APP}/user-tickets/raise-ticket/${id}`,
      ticketRaiseView: (id) => `${ROOTS.APP}/user/raise-ticket/${id}`,
      // periodic-check
      periodicCheckList: `${ROOTS.APP}/user/periodic-checklist`,
    },
    remoteAssets: {
      root: `${ROOTS.APP}/remote-assets`,
      hardware: (id) => `${ROOTS.APP}/remote-assets/hardware/view/${id}`,
      software: (id) => `${ROOTS.APP}/remote-assets/software/view/${id}`,
      others: (id) => `${ROOTS.APP}/remote-assets/others/view/${id}`,
      dynamic: (id, category) => `${ROOTS.APP}/remote-assets/dynamic/${category}/view/${id}`,
    },
    alerts: {
      root: `${ROOTS.APP}/alerts`,
      warranty_subscription: `${ROOTS.APP}/alerts/warranty-subscriptions`,
    },
    reports: {
      root: `${ROOTS.APP}/reports`,
      technicianWiseReports: `${ROOTS.APP}/reports/technician-reports`,
      technicianReportsView: (id) => `/reports/technician-reports/view/${id}`,
      assetDepreciationReports: `${ROOTS.APP}/reports/asset-depreciation-reports`,
      amcContractExpiry: `${ROOTS.APP}/reports/amc-contract-expiry`,
      amcAssetsMaintenance: `${ROOTS.APP}/reports/amc-assets-maintenance`,
      assetSubscriptionListView: `${ROOTS.APP}/reports/asset-subscription`,
      softwareLicenseListView: `${ROOTS.APP}/reports/software-license`,
      assetLifecycleListView: `${ROOTS.APP}/reports/asset-lifecycle`,
      it_support_report: `${ROOTS.APP}/reports/it-support-report`,
    },
    amcContracts: {
      root: `${ROOTS.APP}/amc-contracts`,
      new: `${ROOTS.APP}/amc-contracts/new`,
      view: (id) => `${ROOTS.APP}/amc-contracts/view/${id}`,
    },
    periodicCheck: {
      root: `${ROOTS.APP}/periodic-check`,
      view: (id) => `${ROOTS.APP}/periodic-check/view/${id}`,
      singleUserView: (userId, periodicCheckId) =>
        `${ROOTS.APP}/periodic-check/single-user/view/${userId}?periodicCheckId=${periodicCheckId}`,
      userPeriodicChecklist: `${ROOTS.APP}/periodic-check/user-checklist`,
    },
    NetworkDiscovery: {
      root: `${ROOTS.APP}/network-discovery`,
    },
    DiscoverByImg: {
      root: `${ROOTS.APP}/discover-by-img`,
    },
    asset_audit: {
      root: `${ROOTS.APP}/asset-audit`,
      new: `${ROOTS.APP}/asset-audit/new`,
      view: (id) => `${ROOTS.APP}/asset-audit/view/${id}`,
      edit: (id) => `${ROOTS.APP}/asset-audit/edit/${id}`,
      list: `${ROOTS.APP}/asset-audit/list`,
      verify: (auditId) => `${ROOTS.APP}/asset-audit/start-verification/${auditId}`,
    },
    maintenanceRequests: {
      root: `${ROOTS.APP}/maintenance-requests`,
      new: `${ROOTS.APP}/maintenance-requests/new`,
      list: `${ROOTS.APP}/maintenance-requests/list`,
      details: (id) => `${ROOTS.APP}/maintenance-requests/view/${id}`,
      edit: (id) => `${ROOTS.APP}/maintenance-requests/edit/${id}`,
    },
    workOrder: {
      root: `${ROOTS.APP}/work-order`,
      new: `${ROOTS.APP}/work-order/new`,
      list: `${ROOTS.APP}/work-order/list`,
      calendar: `${ROOTS.APP}/work-order/calendar`,
      details: (id) => `${ROOTS.APP}/work-order/view/${id}`,
      edit: (id) => `${ROOTS.APP}/work-order/edit/${id}`,
    },
    preventiveMaintenance: {
      root: `${ROOTS.APP}/preventive-maintenance`,
      new: `${ROOTS.APP}/preventive-maintenance/new`,
      list: `${ROOTS.APP}/preventive-maintenance/list`,
      calendar: `${ROOTS.APP}/preventive-maintenance/calendar`,
      details: (id) => `${ROOTS.APP}/preventive-maintenance/view/${id}`,
      edit: (id) => `${ROOTS.APP}/preventive-maintenance/edit/${id}`,
    },
    inventory: {
      root: `${ROOTS.APP}/inventory`,
      new: `${ROOTS.APP}/inventory/new`,
      list: `${ROOTS.APP}/inventory/list`,
      details: (id) => `${ROOTS.APP}/inventory/view/${id}`,
      edit: (id) => `${ROOTS.APP}/inventory/edit/${id}`,
    },
    cmms_assets: {
      root: `${ROOTS.APP}/cmms-asset`,
      new: `${ROOTS.APP}/cmms-asset/new`,
      list: `${ROOTS.APP}/cmms-asset/list`,
      details: (id) => `${ROOTS.APP}/cmms-asset/view/${id}`,
      edit: (id) => `${ROOTS.APP}/cmms-asset/edit/${id}`,
    },
  },
};
